import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Eye, Layers, Target, List } from 'lucide-react';
import { useDrawer } from '../components/DrawerSystem';
import PivotPointsView from '../components/PivotPoints/PivotPointsView';
import PivotPointsListView from '../components/PivotPoints/PivotPointsListView';
import PivotPointDetailModal from '../components/PivotPoints/PivotPointDetailModal';
import PivotPointsDrawerContent from '../components/PivotPoints/PivotPointsDrawerContent';
import MemberNodeModal from '../components/PivotPoints/MemberNodeModal';
import PivotMethodSelect from '../components/PivotPoints/PivotMethodSelect';
import StructuralPivotView from '../components/PivotPoints/StructuralPivotView';
import MainViewHeader from '../components/MainViewHeader';
import { ViewModeSelector, ViewModeButton } from '../components/ViewModeSelector';
import { useInstance } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation.jsx';
import API_ENDPOINTS from '../config/api';
import SocketService from '../../../../packages/shared/socket';
import { usePivotData } from '../hooks/usePivotData';
import useContentRefresh from '../hooks/useContentRefresh';
import { computeStructuralMetrics } from '../utils/structuralAnalysis';

// View modes for the pivot points visualization
const VIEW_MODES = {
  ALL: 'tree',      // All single nodes in causal-tree layout (parent-child edges)
  GROUPED: 'grouped', // Pivot points as semantic clusters from RAG
  LIST: 'list',     // Flat scrollable list of pivot points (no spatial layout)
};

/** Space for MainViewHeader (logo + titles + view controls); keeps the map below the chrome. */
const PIVOT_HEADER_RESERVE_PX = 92;

const Container = styled.div`
  width: 100%;
  height: var(--kuutio-vh, 100vh);
  max-height: var(--kuutio-vh, 100vh);
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const HeaderSlot = styled.div`
  flex-shrink: 0;
  z-index: 100;
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 16px;
  padding: 20px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: #ff6b6b;
  font-size: 1rem;
`;

const RetryButton = styled.button`
  background: rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.5);
  color: #4CAF50;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.3);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

/**
 * PivotPointsScreen - Routable wrapper for PivotPointsView
 *
 * Fetches scenarios and nodes data from the backend and renders
 * the D3 force simulation visualization.
 *
 * URL params:
 * - instanceId: Optional instance ID to filter data
 * - demo: If "true", uses mock data for demonstration
 */
function PivotPointsScreen({
  isEmbedded = false,
  screenMode = false
}) {
  const [searchParams] = useSearchParams();
  const { currentInstance } = useInstance();
  const { t } = useTranslation();
  const contentAreaRef = useRef(null);
  const [contentSize, setContentSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? Math.max(0, window.innerHeight - PIVOT_HEADER_RESERVE_PX) : 0,
  });
  // ─── Data layer: fetch, transforms, RAG clusters ───
  const {
    scenarios, nodes, pivotPoints, clusteredNodeIds,
    rawPathways, connections,
    loading, error,
    refetch,
  } = usePivotData({ searchParams, currentInstance });

  const isLocked = !!currentInstance?.settings?.locked;
  const pivotInstanceId = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId') || currentInstance?.id;
  useContentRefresh(pivotInstanceId, ['pathways', 'pivot_edits'], refetch, {
    enabled: !isLocked,
  });

  // ─── View state ───
  // MICMAC / structural (rakenteellinen) pivot analysis is temporarily DISABLED.
  // Flip to true to restore the method toggle + structural (StructuralPivotView) views.
  const STRUCTURAL_ENABLED = false;
  // ?view=pivots|list gives the visualization a direct URL (the dashboard's
  // Vipupisteet summary opens here, specs/workshop-dashboard.md); without it
  // the screen opens on the causal tree as before.
  const [initialParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(() => ({
    pivots: VIEW_MODES.GROUPED,
    grouped: VIEW_MODES.GROUPED,
    list: VIEW_MODES.LIST,
  }[initialParams.get('view')] || VIEW_MODES.ALL));
  const [analysisMethod, setAnalysisMethod] = useState(STRUCTURAL_ENABLED ? 'structural' : 'semantic'); // 'structural' (MICMAC) | 'semantic'
  const { registerTab, unregisterTab } = useDrawer();
  const [selectedPivotId, setSelectedPivotId] = useState(null); // Selected pivot point for detail modal
  const [memberModalNode, setMemberModalNode] = useState(null); // Node to show in detail modal
  const [focusedPivotId, setFocusedPivotId] = useState(null); // Legacy tree-focus — never set, only cleared

  // Default to structural (runtime, always available) until semantic pivots exist;
  // flip to semantic once they're generated, unless the user has chosen manually.
  const methodTouched = useRef(false);
  useEffect(() => {
    if (!methodTouched.current && pivotPoints.length > 0) setAnalysisMethod('semantic');
  }, [pivotPoints.length]);
  const chooseMethod = (m) => { methodTouched.current = true; setAnalysisMethod(m); };

  // Structural (MICMAC) analysis over the raw impact-path DAG — pure runtime.
  const structuralResult = useMemo(
    () => (STRUCTURAL_ENABLED ? computeStructuralMetrics(rawPathways, connections) : null),
    [STRUCTURAL_ENABLED, rawPathways, connections]
  );

  // Size the graph to the content area (not window), so header height and browser chrome stay correct
  useEffect(() => {
    const el = contentAreaRef.current;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setContentSize({ width: Math.round(width), height: Math.round(height) });
      }
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener('resize', update);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
    };
  }, [loading]);

  // Connect to socket for green connection indicator
  // Skip if in screenMode - ScreenClient manages the socket
  useEffect(() => {
    if (isEmbedded || screenMode) return;

    const socketUrl = API_ENDPOINTS.SOCKET_URL();
    SocketService.connect(socketUrl, {
      onConnect: () => {
        console.log('[PivotPoints] Connected to socket');
      },
      onDisconnect: () => {
        console.log('[PivotPoints] Disconnected from socket');
      }
    });

    return () => {
      // Don't disconnect on unmount - let App.jsx handle the connection
    };
  }, [isEmbedded, screenMode]);


  // Derive stable instanceId string — useEffect depends on this, not fetchData
  const instanceId = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId') || currentInstance?.id;

  const handleNodeClick = useCallback((node) => {
    // Pivot cluster → info modal (same as list view). Branch/snowflake drill-downs removed.
    if (node.isClusterOverlay || pivotPoints.some(p => p.id === node.id)) {
      setSelectedPivotId(node.id);
    } else {
      // Regular node - show member detail
      setMemberModalNode(node);
    }
  }, [pivotPoints]);

  const handleNodeHover = useCallback(() => {
    // Hover effects are handled in PivotPointsView
  }, []);

  // Determine which nodes to show based on view mode
  // IMPORTANT: useMemo must be called BEFORE any early returns (React hooks rules)
  // In Grouped view: show pivot points (clusters) + unclustered individual nodes
  // In All view: show all individual nodes
  const { displayNodes, unclusteredCount } = useMemo(() => {
    if (viewMode === VIEW_MODES.ALL) {
      return { displayNodes: nodes, unclusteredCount: 0 };
    }

    const unclusteredNodes = nodes.filter(node => !clusteredNodeIds.has(node.id));
    const clusteredNodes = nodes.filter(node => clusteredNodeIds.has(node.id));

    const backendIds = nodes.slice(0, 3).map(n => n.id);
    const ragIds = Array.from(clusteredNodeIds).slice(0, 3);
    console.log(`📊 ${viewMode} view breakdown:`);
    console.log(`   - Total nodes from backend: ${nodes.length}`);
    console.log(`   - Clustered node IDs from RAG: ${clusteredNodeIds.size}`);
    console.log(`   - Nodes matched as clustered: ${clusteredNodes.length}`);
    console.log(`   - Unclustered nodes: ${unclusteredNodes.length}`);
    console.log(`   - Clusters (pivot points): ${pivotPoints.length}`);
    console.log(`   🔍 Sample backend IDs: ${backendIds.join(', ')}`);
    console.log(`   🔍 Sample RAG cluster IDs: ${ragIds.join(', ')}`);

    console.log(`   - Final displayNodes: ${pivotPoints.length} pivots + ${unclusteredNodes.length} unclustered`);
    return {
      displayNodes: [...pivotPoints, ...unclusteredNodes],
      unclusteredCount: unclusteredNodes.length
    };
  }, [viewMode, nodes, pivotPoints, clusteredNodeIds]);
  const showAllNodesInView = viewMode === VIEW_MODES.ALL;

  // ─── Register pivot points tab in the shared drawer system ───
  // Update tab on every relevant state change (registerTab merges updates).
  // Only unregister on unmount (leaving the screen).
  useEffect(() => {
    return () => unregisterTab('pivotpoints');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerTab({
      id: 'pivotpoints',
      label: t('pivotPoints.drawerTabLabel', { count: pivotPoints.length }),
      icon: <Target size={18} />,
      color: 'rgba(156, 39, 176, 0.3)',
      borderColor: 'rgba(156, 39, 176, 0.5)',
      order: 10,
      render: () => (
        <PivotPointsDrawerContent
          pivotPoints={pivotPoints}
          nodes={nodes}
          scenarios={scenarios}
          clusteredNodeIds={clusteredNodeIds}
          selectedPivotId={selectedPivotId}
          setSelectedPivotId={setSelectedPivotId}
        />
      ),
    });
  }, [pivotPoints, nodes, scenarios, clusteredNodeIds, selectedPivotId, t, registerTab]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
          <p>{t('pivotPoints.loading')}</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderSlot>
      <MainViewHeader
        activeView="pivot_points"
        instanceTitle={currentInstance?.title || ''}
        inDocumentFlow
      >
        {STRUCTURAL_ENABLED && (
        <PivotMethodSelect
          value={analysisMethod}
          onChange={chooseMethod}
          hasSemantic={pivotPoints.length > 0}
        />
        )}
        <ViewModeSelector>
          <ViewModeButton
            $active={viewMode === VIEW_MODES.ALL}
            onClick={() => { setViewMode(VIEW_MODES.ALL); setFocusedPivotId(null); }}
            title={t('pivotPoints.viewMode.allTitle')}
          >
            <Eye />
            {t('pivotPoints.viewMode.impactPaths')} ({nodes.length})
          </ViewModeButton>
          <ViewModeButton
            $active={viewMode === VIEW_MODES.GROUPED}
            onClick={() => { setViewMode(VIEW_MODES.GROUPED); setFocusedPivotId(null); }}
            title={t('pivotPoints.viewMode.clustersTitle')}
          >
            <Layers />
            {t('pivotPoints.viewMode.pivotPoints')} ({pivotPoints.length}+{unclusteredCount || nodes.filter(n => !clusteredNodeIds.has(n.id)).length})
          </ViewModeButton>
          <ViewModeButton
            $active={viewMode === VIEW_MODES.LIST}
            onClick={() => { setViewMode(VIEW_MODES.LIST); setFocusedPivotId(null); }}
            title={t('pivotPoints.viewMode.listTitle')}
          >
            <List />
            {t('pivotPoints.viewMode.list')} ({pivotPoints.length})
          </ViewModeButton>
        </ViewModeSelector>
      </MainViewHeader>
      </HeaderSlot>
      <ContentArea ref={contentAreaRef}>
        {error && (
          <ErrorContainer>
            <ErrorMessage>{t(error)}</ErrorMessage>
            <RetryButton onClick={refetch}>{t('pivotPoints.retry')}</RetryButton>
          </ErrorContainer>
        )}
        {STRUCTURAL_ENABLED && analysisMethod === 'structural' && (viewMode === VIEW_MODES.GROUPED || viewMode === VIEW_MODES.LIST) ? (
          <StructuralPivotView
            result={structuralResult}
            mode={viewMode === VIEW_MODES.LIST ? 'list' : 'grouped'}
            onNodeClick={(node) => setMemberModalNode(node)}
          />
        ) : viewMode === VIEW_MODES.LIST ? (
          <PivotPointsListView
            pivotPoints={pivotPoints}
            instanceId={instanceId}
            onPivotClick={(p) => setSelectedPivotId(p.id)}
          />
        ) : (
          <PivotPointsView
            scenarios={scenarios}
            nodes={displayNodes}
            allNodes={nodes}
            showAllNodes={showAllNodesInView}
            viewMode={viewMode}
            width={contentSize.width}
            height={contentSize.height}
            onNodeClick={handleNodeClick}
            onNodeHover={handleNodeHover}
            focusedPivotId={focusedPivotId}
            onFocusClear={() => setFocusedPivotId(null)}
          />
        )}
      </ContentArea>

      {/* Pivot points drawer content is rendered via DrawerSystem */}

      {/* Pivot Point Detail Modal (shared component) */}
      {selectedPivotId && (() => {
        const selectedPivot = pivotPoints.find(p => p.id === selectedPivotId);
        if (!selectedPivot) return null;
        const modalInstanceId = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId') || currentInstance?.id;
        return (
          <PivotPointDetailModal
            pivotPoint={selectedPivot}
            nodes={nodes}
            scenarios={scenarios}
            instanceId={modalInstanceId}
            onClose={() => setSelectedPivotId(null)}
            onMemberClick={(memberNode) => setMemberModalNode(memberNode)}
          />
        );
      })()}

      {/* Member node detail modal (shared component) */}
      <MemberNodeModal node={memberModalNode} onClose={() => setMemberModalNode(null)} />
    </Container>
  );
}

export default PivotPointsScreen;
